import { cx, cy } from '../game/geometry';
import type { Network, NodeRole, Point } from '../game/types';

/** Anything a wire can terminate on. */
export type AttachRole = NodeRole | 'house';

/**
 * Which drawn view a tower wears. A real tower faces its line — the crossarm
 * runs perpendicular to the wires — so a mostly north-south line meets the
 * camera head-on (the classic front view), while a mostly east-west line
 * shows its towers in profile: a narrow mast with stacked, staggered arms.
 */
export type Facing = 'front' | 'side';

export interface Vec2 {
  x: number;
  y: number;
}

type Conductors = { L: [number, number]; C: [number, number]; R: [number, number] };

/**
 * Each sprite's conductor points, as offsets from the cell centre.
 *
 * L/C/R must coincide with the amber conductor dots drawn on the sprite —
 * if a sprite moves, these move with it or the wires float. Every position
 * below is the RENDERED coordinate after the sprite's own scale (sprites.ts
 * `SPRITE_SCALE` for plant/house/pylon, and the sx/sy passed to
 * drawCatFaceTower for the junction and crossing giant) has been applied.
 */
const CONDUCTORS: Record<AttachRole, Conductors> = {
  // the takeoff beam's three insulators, (4 | 12 | 20, -28) × 1.4
  plant: { L: [5.6, -39.2], C: [16.8, -39.2], R: [28, -39.2] },
  // the suspension tower's arm-tip insulators + the peak, ×1.4
  standard: { L: [-24.5, -29.82], C: [0, -39.48], R: [24.5, -29.82] },
  // outer V-strings under the crossarms + the wishbone in the head. Junction
  // renders with sx=sy=1.4*0.9=1.26 (X = 18*1.26 = 22.68; Y outer is
  // (-3-34)*1.26+34 = -12.62; Y centre is (-31-34)*1.26+34 = -47.9)
  junction: { L: [-22.68, -12.62], C: [0, -47.9], R: [22.68, -12.62] },
  // crossing giant: uniform sx=sy=1.4*1.02=1.428 about ground=34
  crossing: { L: [-25.7, -18.84], C: [0, -58.82], R: [25.7, -18.84] },
  // dead-end anchor, for the single-wire pickers: the main arms' tips
  // (±16, -13) and the hat's top-beam centre (0, -42), at the junction's
  // own sx=sy=1.26 mapping. Bundles use DEADEND_TIERS below.
  deadend: { L: [-20.16, -25.22], C: [0, -61.76], R: [20.16, -25.22] },
  // the service mast, (0, -30) × 1.4
  house: { L: [0, -42], C: [0, -42], R: [0, -42] },
};

/**
 * The profile views' conductor points, [mid, top, bottom] so index 1 is the
 * highest (the spark's pair, like every other set). A tower seen edge-on
 * carries its line the way a real one photographs from beside the
 * right-of-way: the crossarm points at the camera, so everything hangs on
 * the mast's own centreline — the centre phase high (the peak, or the
 * wishbone in the cat-face's head, at exactly the height the front view
 * hangs it) and the two outer phases in line below the arm, one bead behind
 * the other. An east-west span therefore reads as one high wire over a
 * tight pair, which is what the real thing looks like. Offsets are rendered
 * coordinates after each sprite's scale. Base coords, all at x = 0:
 * standard -12.5 | -28.2 | -9; junction/crossing -4.6 | -31 | -1.4,
 * × each role's scale.
 */
const SIDE_CONDUCTORS: Partial<Record<AttachRole, [number, number][]>> = {
  standard: [
    [0, -17.5],
    [0, -39.48],
    [0, -12.6],
  ],
  junction: [
    [0, -14.64],
    [0, -47.9],
    [0, -10.6],
  ],
  crossing: [
    [0, -21.12],
    [0, -58.82],
    [0, -16.55],
  ],
};

/**
 * A dead-end tower terminates each span on the side facing it, the way a
 * real strain giant does: one conductor on the short upper crossarm's tip,
 * two on the long main arm — its tip and an inner string — with the jumper
 * loops slung beneath. Each side is ordered [mid, top, bottom] so index 1
 * is the highest conductor, matching every other role's [L, C, R] where C
 * rides the peak — the pair leaving the near node's index 1 is the one the
 * surge spark rides. Tips are the upper arm (±11, -32) and the main arm's
 * tip and inner string (±16 | ±9, -13), at the junction's own sx=sy=1.26
 * mapping — the dead-end stands on the cat-face's base at its height. The
 * main-arm pair shares a height on purpose: a span along the arm's own axis
 * stacks them the way the real pair photographs, and the pairing keeps
 * them uncrossed.
 */
const DEADEND_TIERS: Record<'left' | 'right', [number, number][]> = {
  left: [
    [-20.16, -25.22],
    [-13.86, -49.16],
    [-11.34, -25.22],
  ],
  right: [
    [20.16, -25.22],
    [13.86, -49.16],
    [11.34, -25.22],
  ],
};

/**
 * Beyond this horizontal offset a wire lands on an arm tip rather than the
 * peak. Sized so a same-row neighbour (dx = 100) always lands on an arm, but
 * the split direction still tracks the wire's actual bearing when it comes
 * in near-vertically.
 */
const ARM_THRESHOLD = 45;

/**
 * Which view each node wears this frame. A tower votes with the spans it
 * carries: mostly east-west spans turn it to the profile view, mostly
 * north-south keep it head-on, diagonals abstain, and a tie stays head-on.
 * House drops don't vote — a service cable follows the tower, never the
 * other way round. Roles without a profile drawing ignore the result.
 */
export function towerFacings(net: Network): Facing[] {
  const votes = net.nodes.map(() => 0);
  for (const e of net.edges) {
    const v = Math.sign(Math.abs(cx(e.b) - cx(e.a)) - Math.abs(cy(e.b) - cy(e.a)));
    votes[e.ai] += v;
    votes[e.bi] += v;
  }
  return votes.map((v) => (v > 0 ? 'side' : 'front'));
}

/** Where a single wire (house drop, reference line) meets a sprite. */
export function attachPoint(
  n: Point,
  role: AttachRole,
  towardX: number,
  facing: Facing = 'front',
): Vec2 {
  const dx = towardX - cx(n);
  const side = facing === 'side' ? SIDE_CONDUCTORS[role] : undefined;
  if (side) {
    // profile terminals all ride the centreline: a sideways wire taps the
    // low arm-pair bead, a near-vertical one the high centre phase
    const p = Math.abs(dx) > ARM_THRESHOLD ? side[2] : side[1];
    return { x: cx(n) + p[0], y: cy(n) + p[1] };
  }
  const t = CONDUCTORS[role];
  const p = dx < -ARM_THRESHOLD ? t.L : dx > ARM_THRESHOLD ? t.R : t.C;
  return { x: cx(n) + p[0], y: cy(n) + p[1] };
}

/**
 * All three phase points on a node: the fixed [L, C, R] trio for most roles,
 * the profile view's staggered-arm stack when the tower faces its line that
 * way, or a dead-end's facing-side tiers (picked by where the span is
 * heading, like a real strain termination). Index 1 is always the highest
 * point, so spanConductors' near-side pair order puts the surge spark on top.
 */
function conductorPoints(
  n: Point,
  role: AttachRole,
  facing: Facing,
  towardX: number,
): [Vec2, Vec2, Vec2] {
  const at = (p: [number, number]): Vec2 => ({ x: cx(n) + p[0], y: cy(n) + p[1] });
  if (role === 'deadend') {
    const [a, b, c] = DEADEND_TIERS[deadendSide(n, towardX)];
    return [at(a), at(b), at(c)];
  }
  const side = facing === 'side' ? SIDE_CONDUCTORS[role] : undefined;
  if (side) return [at(side[0]), at(side[1]), at(side[2])];
  const t = CONDUCTORS[role];
  return [at(t.L), at(t.C), at(t.R)];
}

/**
 * Which face of a dead-end anchor a span heading toward `towardX` lands on.
 * The board uses this to show terminal hardware only on faces holding wire.
 */
export function deadendSide(n: Point, towardX: number): 'left' | 'right' {
  return towardX < cx(n) ? 'left' : 'right';
}

const PERMS: [number, number, number][] = [
  [0, 1, 2],
  [0, 2, 1],
  [1, 0, 2],
  [1, 2, 0],
  [2, 0, 1],
  [2, 1, 0],
];

/**
 * A sign swap shallower than this many board units on either flank is a
 * graze, not a crossing — two strokes within a tenth of a unit read as one
 * line, and treating a numerical tangency as an X would send the pairing
 * hunting for a worse matching than the one the eye prefers.
 */
const CROSS_DEPTH = 0.1;

/** Min and max of the quadratic ax² + bx + c over [lo, hi]. */
function quadRange(a: number, b: number, c: number, lo: number, hi: number): [number, number] {
  const at = (x: number) => (a * x + b) * x + c;
  let min = Math.min(at(lo), at(hi));
  let max = Math.max(at(lo), at(hi));
  if (a !== 0) {
    const v = -b / (2 * a);
    if (v > lo && v < hi) {
      min = Math.min(min, at(v));
      max = Math.max(max, at(v));
    }
  }
  return [min, max];
}

/**
 * Do two conductors of one span, drawn with the shared span sag, visibly
 * cross? Exact, not sampled: wirePath's control point sits at the chord's
 * mid-x, so x(t) is linear and each conductor is y(x) = lerp(y1,y2) +
 * 2S·t(1−t) — a quadratic in x. Their difference D(x) is itself quadratic,
 * and the curves swap sides iff D leaves ±CROSS_DEPTH in both directions
 * inside the shared x-range. Touching, tangency and grazes don't count,
 * only an actual X.
 */
export function conductorsCross(p: [Vec2, Vec2], q: [Vec2, Vec2], sagLen: number): boolean {
  const S = sagDepth(sagLen);

  // y(x) = A x² + B x + C for a non-vertical conductor
  const coeffs = ([a, b]: [Vec2, Vec2]): [number, number, number] | null => {
    const dx = b.x - a.x;
    if (dx === 0) return null;
    const k = 1 / dx; // t = k (x − a.x)
    const lin = (b.y - a.y + 2 * S) * k;
    const A = -2 * S * k * k;
    return [A, lin - 2 * A * a.x, a.y - lin * a.x + A * a.x * a.x];
  };

  const cp = coeffs(p);
  const cq = coeffs(q);
  const cc = cp ?? cq;
  if (!cc) return false; // two verticals: parallel or collinear, never an X

  if (!cp || !cq) {
    // one vertical conductor: the other crosses it iff its height where they
    // share x falls properly inside the vertical curve's own y-extent
    const vert = cp ? q : p;
    const other = cp ? p : q;
    const x = vert[0].x;
    const lo = Math.min(other[0].x, other[1].x);
    const hi = Math.max(other[0].x, other[1].x);
    if (x <= lo || x >= hi) return false;
    const yq = (cc[0] * x + cc[1]) * x + cc[2];
    // the vertical curve is y(t) = lerp + 2S·t(1−t) over t ∈ [0, 1]
    const [vMin, vMax] = quadRange(-2 * S, vert[1].y - vert[0].y + 2 * S, vert[0].y, 0, 1);
    return yq > vMin + CROSS_DEPTH && yq < vMax - CROSS_DEPTH;
  }

  const lo = Math.max(Math.min(p[0].x, p[1].x), Math.min(q[0].x, q[1].x));
  const hi = Math.min(Math.max(p[0].x, p[1].x), Math.max(q[0].x, q[1].x));
  if (hi - lo <= 0) return false;

  const [dMin, dMax] = quadRange(cp[0] - cq[0], cp[1] - cq[1], cp[2] - cq[2], lo, hi);
  return dMin < -CROSS_DEPTH && dMax > CROSS_DEPTH;
}

/**
 * The three conductor pairs of a span, matched for minimum total length
 * among the matchings whose *drawn* curves stay untangled. Real conductors
 * don't detour, and a twisted matching that only crosses once sagged is
 * still a twist — so the tangle test runs on the sagging curves, not the
 * straight chords. On the rare bearing where the fixed terminals allow no
 * clean matching at all, the shortest overall is drawn. Pair order follows
 * the near node's trio, so pairs[1] always leaves its centre (highest)
 * point — the surge spark rides that one.
 */
export function spanConductors(
  a: Point,
  roleA: AttachRole,
  facingA: Facing,
  b: Point,
  roleB: AttachRole,
  facingB: Facing,
): [Vec2, Vec2][] {
  const sagLen = Math.hypot(cx(b) - cx(a), cy(b) - cy(a));
  const pa = conductorPoints(a, roleA, facingA, cx(b));
  const pb = conductorPoints(b, roleB, facingB, cx(a));
  let best: [Vec2, Vec2][] = [];
  let bestClean = false;
  let bestLen = Infinity;
  for (const perm of PERMS) {
    const pairs = perm.map((bi, ai): [Vec2, Vec2] => [pa[ai], pb[bi]]);
    let len = 0;
    for (const [p, q] of pairs) len += Math.hypot(q.x - p.x, q.y - p.y);
    const clean =
      !conductorsCross(pairs[0], pairs[1], sagLen) &&
      !conductorsCross(pairs[0], pairs[2], sagLen) &&
      !conductorsCross(pairs[1], pairs[2], sagLen);
    if (clean === bestClean ? len < bestLen : clean) {
      best = pairs;
      bestClean = clean;
      bestLen = len;
    }
  }
  return best;
}

/**
 * The sag depth for a span of length `len` — the constants' one true home.
 * Both the drawn control point and conductorsCross's closed-form coefficients
 * read it, so the exact crossing test can never certify a curve the renderer
 * is not drawing (it silently did exactly that once, when the formula lived
 * in two places and one was retuned).
 */
const sagDepth = (len: number): number => 6 + len * 0.055;

/**
 * The quadratic's control point. A span's three conductors share its
 * centre-to-centre sag length, so the bundle hangs as one unit; a lone wire
 * defaults to its own length.
 */
function sagControl(p1: Vec2, p2: Vec2, sagLen?: number): Vec2 {
  const len = sagLen ?? Math.hypot(p2.x - p1.x, p2.y - p1.y);
  return { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 + sagDepth(len) };
}

/** A catenary-ish quadratic — longer spans sag more. */
export function wirePath(p1: Vec2, p2: Vec2, sagLen?: number): string {
  const m = sagControl(p1, p2, sagLen);
  return `M${p1.x} ${p1.y} Q${m.x} ${m.y} ${p2.x} ${p2.y}`;
}

/** The exact curve wirePath draws, evaluated at t ∈ [0, 1]. */
export function pointOnWire(p1: Vec2, p2: Vec2, t: number, sagLen?: number): Vec2 {
  const m = sagControl(p1, p2, sagLen);
  const u = 1 - t;
  return {
    x: u * u * p1.x + 2 * u * t * m.x + t * t * p2.x,
    y: u * u * p1.y + 2 * u * t * m.y + t * t * p2.y,
  };
}

/** Arc length of the drawn curve, close enough to phase dashes by. */
export function wireArcLength(p1: Vec2, p2: Vec2, sagLen?: number): number {
  let len = 0;
  let prev = p1;
  for (let i = 1; i <= 16; i++) {
    const p = pointOnWire(p1, p2, i / 16, sagLen);
    len += Math.hypot(p.x - prev.x, p.y - prev.y);
    prev = p;
  }
  return len;
}
